import React, {useEffect, useState} from "react"
import {useDispatch} from "react-redux"
import {useNavigate} from "react-router-dom"
import {
   ArrowBack as ArrowBackIcon,
   Add as AddIcon,
   Delete as DeleteIcon,
   Edit as EditIcon,
   AttachFile as AttachFileIcon,
} from "@mui/icons-material"
import {
   Box,
   Button,
   TextField,
   InputLabel,
   MenuItem,
   FormControl,
   Select,
   Chip,
   Alert,
} from "@mui/material"
import type {SelectChangeEvent} from "@mui/material/Select"
import DataRow from "@/components/ui/DataRow"
import Page from "@/layouts/Page"
import UploadImagesForm from "@/components/ui/UploadImagesForm"
import ConfirmationDialog from "@/components/ui/ConfirmationDialog"
import BooleanToggle from "@/components/properties/BooleanToggle"
import {postProperty, uploadPropertyImages} from "@/api/properties"
import {condosRtkApi} from "@/api/condo"
import {checkNameTaken} from "@/api/utils"
import {getCondoLinks} from "@/api/condo"
import {getAssociates} from "@/api/asociados"
import {ReactSortable} from "react-sortablejs"
import {nanoid} from "nanoid"
import {
   environments as envs,
   furnishTypes,
   PropertyStatus,
   PropertyType,
   AcapulcoPropertyDoc,
   RegionCode,
   isHouseLikePropertyType,
} from "@casas-diamante/types"
import {clearCache, displayZoneLabel, isMobile} from "@/utils"
import {regions, zonesByRegion} from "@casas-diamante/types"
import AssociatesSelect from "@/components/properties/AssociatesSelect"
import {useVideoList} from "@/hooks/useVideoList"
import {prepareSeaviewForStorage, type SeaviewOption} from "@/types/Seaview"
import {OpportunityTag} from "@casas-diamante/types"

interface Condominio {
   value: string
   label: string
}

interface Partners {
   value: string
   label: string
}

interface SortableSectionsType {
   name: string
   id: string
}

function AddProperty() {
   const navigate = useNavigate()
   const dispatch = useDispatch()
   const [error, setError] = useState<boolean | string>(false)
   const [condos, setCondos] = useState<Condominio[]>()
   const [region, setRegion] = useState<RegionCode>(RegionCode.ACA)
   const [name, setName] = useState<null | string>(null)
   const [status, setStatus] = useState<PropertyStatus>(PropertyStatus.Verified)
   const [desc, setDesc] = useState<null | string>(null)
   const [desc2, setDesc2] = useState<null | string>(null)
   const [urlCAD, setUrlCad] = useState<null | string>(null)
   const [condo, setCondo] = useState<null | string>(null)
   const [type, setType] = useState<PropertyType>(PropertyType.House)
   const [capacity, setCapacity] = useState<null | number>(null)
   const [mts, setMts] = useState<null | number>(null)
   const [mtsContruccion, setMtsContruccion] = useState<null | number>(null)
   const [floor, setFloor] = useState<null | number>(null)
   const [levels, setLevels] = useState<null | number>(null)
   const [rooms, setRooms] = useState<null | number>(null)
   const [bathrooms, setBathrooms] = useState<null | number>(null)
   const [parkingLots, setParkingLots] = useState<null | number>(null)
   const [mensual_anual, setMensualAnual] = useState<null | number>(null)
   const [mensual_regular, setMensualRegular] = useState<null | number>(null)
   const [noche_alta, setNocheAlta] = useState<null | number>(null)
   const [noche_media, setNocheMedia] = useState<null | number>(null)
   const [noche_regular, setNocheRegular] = useState<null | number>(null)
   const [costoVenta, setCostoVenta] = useState<null | number>(null)
   const [seaview, setSeaview] = useState<SeaviewOption>(null)
   const [furnished, setFurnished] = useState<string>("")
   const [furnishType, setFurnishType] = useState<string>("")
   const [mapsUrl, setMapsUrl] = useState<string>("")
   const [mapsShareUrl, setMapsShareUrl] = useState<string>("")
   const [maintenance, setMaintenace] = useState<null | number>(null)
   const [zone, setZone] = useState<null | string>("")
   const [environments, setEnvironments] = useState<string[]>([])
   const [outingToTheBeach, setOutingToTheBeach] = useState<string>("")
   const [houseAntiquity, setHouseAntiquity] = useState<null | string>(null)
   const [available, setAvailable] = useState<string>("true")
   const [suspended, setSuspended] = useState<string>("false")
   const [notes, setNotes] = useState<null | string>(null)
   const [privateNotes, setPrivateNotes] = useState<null | string>(null)
   const [ownerNotes, setOwnerNotes] = useState<null | string>(null)
   const [partners, setPartners] = useState<string[]>([])
   const [partnersFetched, setPartnersFetched] = useState<Partners[]>([])
   const [address, setAddress] = useState<null | string>(null)
   const [petFriendly, setPetFriendly] = useState<string>("")
   const [file, setFile] = useState<File | null>(null)
   const [showImageForm, setShowImageForm] = useState<boolean>(false)
   const [categoria, setCategoria] = useState<string>("")
   const [categories, setCategories] = useState<Array<SortableSectionsType>>([])
   const [currImage, setCurrImage] = useState<string>("")
   const [images, setImages] = useState<any>()
   const [loading, setLoading] = useState<boolean>(false)
   const [confirmationOpen, setConfirmationOpen] = useState<boolean>(false)
   const [categoryToDelete, setCategoryToDelete] = useState(0)
   const [saleComission, setSaleComission] = useState<number | null>(null)
   const [opportunityTag, setPriceTag] = useState<OpportunityTag | null>(null)
   const {videoInput, setVideoInput, videosList, addVideo, removeVideo} = useVideoList({
      onError: setError,
   })

   useEffect(() => {
      if (!error) {
         return
      }

      window.scrollTo({top: 0, behavior: "smooth"})
   }, [error])

   // Booleans to make input validation easier
   const isHouse = isHouseLikePropertyType(type)
   const isApartment = type === PropertyType.Apartment
   const isCommercial = type === PropertyType.Commercial
   const isLand = type === PropertyType.Land
   const acapulcoSelected = region === RegionCode.ACA
   const saleArea = isLand ? mts : mtsContruccion

   // in order to update the enviroment state, which is an string array
   const handleChangeEnvironments = (event: SelectChangeEvent<typeof environments>) => {
      const {
         target: {value},
      } = event
      setEnvironments(
         // On autofill we get a stringified value.
         typeof value === "string" ? value.split(",") : value,
      )
   }

   const handleChangePartners = (event: SelectChangeEvent<(string | number)[]>) => {
      const {
         target: {value},
      } = event
      // Convert all values to strings to match the partners state type
      setPartners(typeof value === "string" ? value.split(",") : value.map(String))
   }

   const imageHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
      setFile(null)
      setError(false)
      if (!e.target.files) {
         setError("Selecciona un imagen")
         return
      }
      if (!e.target.files[0].type.includes("image")) {
         setError("Solo se aceptan imagenes")
         return
      }
      if (
         e.target.files[0].name.endsWith("jpg") ||
         e.target.files[0].name.endsWith("png") ||
         e.target.files[0].name.endsWith("jpeg") ||
         e.target.files[0].name.endsWith("JPEG") ||
         e.target.files[0].name.endsWith("JPG") ||
         e.target.files[0].name.endsWith("PNG")
      ) {
         setFile(e.target.files[0])
         setError(false)
      } else {
         setError("Solo se aceptan imagenes jpeg o png")
         return
      }
   }

   // Whenever region changes, change the fetched condos and clear corresponding fields
   useEffect(() => {
      const fetchCondos = async () => {
         const res = await getCondoLinks(region)
         const condOptions: Condominio[] = []
         res.forEach((cond: any) => {
            condOptions.push({value: cond.id, label: cond.name})
         })
         setCondos(condOptions)
      }

      setCondo(null)
      setZone(null)
      if (!acapulcoSelected) {
         setOutingToTheBeach("")
         setSeaview(null)
      }

      fetchCondos()
   }, [region])

   // Fetch available partners for the multi select
   useEffect(() => {
      const fetchPartners = async () => {
         const res = await getAssociates()
         const associates: Partners[] = []
         res.forEach((ass: any) => {
            associates.push({value: ass.id, label: ass.name})
         })
         setPartnersFetched(associates)
      }
      fetchPartners()
   }, [])

   // everytime a user adds a category, we add it to the categories state
   // also it is added to the images object that has this format:
   // images ={
   //   "categoria1": ["url1", "url2"],
   //   "categoria2": ["url3", "url4"]
   // }
   const handleCategoriasChange = () => {
      setError(false)
      // if no category was added then an error is set
      if (categoria === "") {
         setError("Ingresa una categoria")
         return
      }
      // if the category list already exists we just add the new category to it
      // and also to the images object, which will be empty until
      // it is change in the backend
      if (categories) {
         setCategories((prevCategories) => [
            ...prevCategories,
            {
               id: nanoid(),
               name: categoria.trim(),
            },
         ])
         setImages((prevImages: any) => {
            if (!prevImages) {
               return {
                  [categoria.trim()]: [],
               }
            }
            const keys = Object.keys(prevImages)
            keys.push(categoria.trim())
            const newImages: any = {}
            for (let i = 0; i < keys.length; i++) {
               newImages[keys[i]] = images[keys[i]] ? images[keys[i]] : []
            }
            return newImages
         })
      }
      setCategoria("")
   }

   // everytime the user deletes a category, we remove it from the categories state
   const handleCategoriaDelete = (index: number) => {
      setImages((prevImages: any) => {
         delete prevImages[categories[index].name]
         return prevImages
      })
      const categoriesUpdated = categories.filter((video, i) => i !== index)
      setCategories(categoriesUpdated)
   }

   // when the user decides to edit a category and add images this function
   // is called, it sets the currImage state to the category that is being edited
   // and also sets the showImageForm state to true, which will show the image form
   const handleImageEdit = (index: number) => {
      setCurrImage(categories[index].name)
      setShowImageForm(true)
   }

   const closeImageForm = () => {
      setShowImageForm(false)
   }

   // when the user clicks the upload images button in the image form
   // this function is called, it adds the images to the images state
   // in its respective category and also sets the showImageForm state to false
   const addImages = (images: any, imageCategory: string) => {
      setImages((prevImages: any) => {
         prevImages[imageCategory] = [...images]
         return prevImages
      })
      setShowImageForm(false)
   }

   const handleTypeChange = async (e: SelectChangeEvent<string>) => {
      setType(e.target.value as PropertyType)
      // Reset environments if the type is changed
      const newEnvs: string[] = []
      for (let i = 0; i < envs.length; i++) {
         environments.forEach((env) => {
            if (env === envs[i]) {
               newEnvs.push(env)
            }
         })
      }
      setEnvironments(newEnvs)
   }

   const handleSubmit = async () => {
      setError(false)
      setLoading(true)
      // Check for every required field to be filled
      if (!name || name === "") {
         setLoading(false)
         setError("Elija un nombre")
         return
      }
      const nameTaken = await checkNameTaken(name, "inmuebles")
      if (nameTaken) {
         setLoading(false)
         setError(
            "El nombre de este inmueble ya está tomado. Favor de especificar otro nombre o contactar al administrador para notificar de un problema.",
         )
         return
      }
      if (isApartment && (!condo || condo === "")) {
         setLoading(false)
         setError("Elija un condominio")
         return
      }
      if (!images) {
         setLoading(false)
         setError("Agregue una o mas categorías de imágenes")
         return
      }
      if (acapulcoSelected && !seaview) {
         setLoading(false)
         setError("Elija si tiene vista al mar")
         return
      }
      if (!file) {
         setLoading(false)
         setError("Agrega la imagen principal")
         return
      }
      if (isHouse && mapsShareUrl === "") {
         setLoading(false)
         setError("Como es casa o especial, el URL de Google Maps para compartir es obligatorio")
         return
      }
      if (!condo && zone === "") {
         setLoading(false)
         setError("Elija una zona porque no hay condominio asignado")
         return
      }

      const keys = Object.keys(images)
      let specificImageError = false
      for (let i = 0; i < keys.length; i++) {
         if (images[keys[i]].length <= 0) {
            setError(`Agregue una o mas imágenes a la categoría ${keys[i]}`)
            setLoading(false)
            specificImageError = true
            return
         }
      }
      if (specificImageError) {
         setLoading(false)
         return
      }
      // create the images object with the categorias fields
      // this fields are empty arrays at the moment
      const categoriesVacias: any = {}
      for (let i = 0; i < categories.length; i++) {
         categoriesVacias[categories[i].name] = []
      }
      const seaviewValue = acapulcoSelected ? prepareSeaviewForStorage(seaview) : null
      const furnishedBool = furnished === "true" ? true : false
      const outingToTheBeachBool = acapulcoSelected
         ? outingToTheBeach === "true"
            ? true
            : false
         : null
      // create the inmueble object with all the data
      // a check is done, so that if the user did not add a field
      // null is the default value
      const inmueble: any = {
         name,
         region,
         status,
         desc: desc === "" ? null : desc?.trim(),
         desc2: desc2 === "" ? null : desc2?.trim(),
         urlCAD: urlCAD === "" ? null : urlCAD,
         condo: condo,
         type,
         capacity: capacity === 0 ? null : capacity,
         mts: mts === 0 ? null : mts,
         mtsConstruction: mtsContruccion === 0 ? null : mtsContruccion,
         floor: floor === 0 || isHouse ? null : floor,
         levels: levels === 0 ? null : levels,
         rooms: rooms === 0 ? null : rooms,
         bathrooms: bathrooms === 0 ? null : bathrooms,
         parkingLots: parkingLots === 0 ? null : parkingLots,
         costos: {
            mensual_anual: mensual_anual === 0 ? null : mensual_anual,
            mensual_regular: mensual_regular === 0 ? null : mensual_regular,
            noche_alta: noche_alta === 0 ? null : noche_alta,
            noche_media: noche_media === 0 ? null : noche_media,
            noche_regular: noche_regular === 0 ? null : noche_regular,
         },
         costoVenta: costoVenta ? costoVenta : null,
         videos: videosList.length <= 0 ? null : videosList,
         images: categoriesVacias,
         seaview: seaviewValue,
         furnished: isLand ? null : furnishedBool,
         pricePerMeter:
            saleArea === 0 || !saleArea || costoVenta === 0 || !costoVenta
               ? null
               : costoVenta / saleArea,
         maintenance: maintenance === 0 ? null : maintenance,
         environments:
            isHouse || isApartment ? (environments.length > 0 ? environments : null) : null,
         outing_to_the_beach: outingToTheBeachBool,
         house_antiquity: houseAntiquity === "" || !isHouse ? null : houseAntiquity,
         mainImage: null,
         orderedSections: categories.map((category) => category.name),
         available: available === "true" ? true : false,
         suspended: suspended === "true" ? true : false,
         notes: notes === "" ? null : notes,
         privateNotes: privateNotes === "" ? null : privateNotes,
         ownerNotes: ownerNotes === "" ? null : ownerNotes,
         partners: partners.length <= 0 ? null : partners,
         address: address === "" ? null : address,
         furnishType: furnishType === "" ? null : furnishType,
         mapsUrl: mapsUrl === "" ? null : mapsUrl,
         mapsShareUrl: mapsShareUrl === "" ? null : mapsShareUrl,
         zone: zone === "" ? null : zone,
         // Set boolean scheme values depending on price presence.
         // For rent boolean is set after costos has been set
         forSale: costoVenta ? true : false,
         petFriendly: petFriendly === "" ? null : petFriendly === "true" ? true : false,
         saleComission: saleComission ? saleComission : null,
         opportunityTag: opportunityTag,
      }
      const inmuebleFields = Object.keys(inmueble)
      for (const key in inmuebleFields) {
         if (
            (inmueble[inmuebleFields[key]] === null ||
               inmueble[inmuebleFields[key]] === undefined) &&
            inmuebleFields[key] !== "mainImage"
         ) {
            delete inmueble[inmuebleFields[key]]
         }
      }
      const costosFields = Object.keys(inmueble.costos)
      for (const key in costosFields) {
         if (
            inmueble.costos[costosFields[key]] === null ||
            inmueble.costos[costosFields[key]] === undefined
         ) {
            delete inmueble.costos[costosFields[key]]
         }
      }

      const newCostos = Object.keys(inmueble.costos)
      if (newCostos.length <= 0) {
         delete inmueble.costos
      }

      // Set forRent boolean scheme depending on costos presence
      if (inmueble.costos) {
         inmueble.forRent = true
      } else {
         inmueble.forRent = false
      }

      let postedProperty: AcapulcoPropertyDoc | null = null
      try {
         postedProperty = await postProperty(inmueble)
         dispatch(condosRtkApi.util.invalidateTags([{type: "CondoPriceAnalysis", id: "LIST"}]))
      } catch (error) {
         setLoading(false)
         setError(error.response?.data?.errors[0]?.msg || "Error desconocido al crear el inmueble")
         return
      }

      // Here the property was created, so we can proceed to upload images
      try {
         const formData = new FormData()
         const keys = Object.keys(images)
         for (let i = 0; i < keys.length; i++) {
            for (let j = 0; j < images[keys[i]].length; j++) {
               formData.append(`${keys[i]}`, images[keys[i]][j])
            }
         }
         formData.append("mainImage", file as Blob)
         await uploadPropertyImages(formData, postedProperty?.id)

         // Success
         setLoading(false)
         clearCache(["properties"])
         navigate(`/inmuebles/${postedProperty?.id}`)
      } catch (error) {
         // Image upload failed, try to delete the property
         setLoading(false)
         alert(
            "El inmueble fue creado, pero las imágenes no se pudieron subir. Por favor cárgalas en la página de edición del inmueble y notifica al desarrollador para que revisen el problema de inmediato.",
         )
         clearCache(["properties"])
         navigate(`/inmuebles/${postedProperty?.id}`)
      }
   }

   return (
      <Page>
         <>
            {!showImageForm && (
               <ArrowBackIcon
                  className="font-bold mb-7 cursor-pointer mt-5"
                  onClick={() => navigate(-1)}
               />
            )}
            <div className="mb-5 flex flex-col max-w-[1600px] mx-auto px-4 md:px-[5%] gap-4">
               {!showImageForm ? (
                  <>
                     <h3 className="text-center text-2xl text-blue font-bold mt-5 mb-5">
                        Agregar Inmueble
                     </h3>
                     {error && (
                        <Alert
                           severity="error"
                           className="text-center text-2xl mb-5 w-full md:w-[30%] mx-auto"
                        >
                           {error}
                        </Alert>
                     )}
                     <h3 className="text-blue mb-2">Información general</h3>
                     <DataRow>
                        <TextField
                           size="small"
                           label="Nombre de propiedad *"
                           fullWidth
                           value={name ? name : ""}
                           onChange={(e) => setName(e.target.value)}
                        />
                        <FormControl size="small" fullWidth sx={{margin: "0px 2%"}}>
                           <InputLabel>Región *</InputLabel>
                           <Select
                              label="Región *"
                              value={region}
                              onChange={(e) => setRegion(e.target.value as RegionCode)}
                           >
                              {regions.map((region) => (
                                 <MenuItem key={region.code} value={region.code}>
                                    {region.text}
                                 </MenuItem>
                              ))}
                           </Select>
                        </FormControl>
                        <FormControl size="small" fullWidth>
                           <InputLabel>Zona</InputLabel>
                           <Select
                              label="Zona"
                              value={zone}
                              disabled={condo ? true : false}
                              onChange={(e) => setZone(e.target.value)}
                              data-testid="zone-select"
                           >
                              {zonesByRegion[region].map((z: string, i) => (
                                 <MenuItem value={z} key={z} data-testid={`zone-${i}`}>
                                    {displayZoneLabel(z)}
                                 </MenuItem>
                              ))}
                           </Select>
                        </FormControl>
                     </DataRow>
                     <DataRow>
                        <BooleanToggle
                           label={
                              suspended === "true"
                                 ? "Apagado"
                                 : suspended === undefined
                                 ? "Sin actualizar"
                                 : "Encendido"
                           }
                           onPress={() => {
                              const nextSuspended = suspended === "true" ? "false" : "true"
                              setSuspended(nextSuspended)
                              if (nextSuspended === "true") setAvailable("false")
                           }}
                           condition={suspended === "false"}
                        />
                        <BooleanToggle
                           label={
                              available === "true"
                                 ? "Disponible"
                                 : available === undefined
                                 ? "Sin actualizar"
                                 : "No disponible"
                           }
                           onPress={() => setAvailable(available === "true" ? "false" : "true")}
                           condition={available === "true"}
                           disabled={suspended === "true"}
                        />
                        <FormControl size="small">
                           <InputLabel shrink>Etiqueta de oportunidad</InputLabel>
                           <Select
                              displayEmpty
                              notched
                              value={opportunityTag ?? ""}
                              label="Etiqueta de oportunidad"
                              onChange={(e) =>
                                 setPriceTag(
                                    e.target.value === ""
                                       ? null
                                       : (e.target.value as OpportunityTag),
                                 )
                              }
                           >
                              <MenuItem value="">Sin etiqueta</MenuItem>
                              <MenuItem value={OpportunityTag.Opportunity}>
                                 Oportunidad (15-25%)
                              </MenuItem>
                              <MenuItem value={OpportunityTag.UniqueOpportunity}>
                                 Oportunidad única (25%&lt;)
                              </MenuItem>
                           </Select>
                        </FormControl>
                        <FormControl size="small">
                           <InputLabel>Estatus *</InputLabel>
                           <Select
                              displayEmpty
                              disabled={isLand}
                              value={status}
                              onChange={(e) => setStatus(e.target.value as PropertyStatus)}
                              className="h-[38px]"
                              label="Estatus *"
                           >
                              {Object.values(PropertyStatus).map((s, i) => (
                                 <MenuItem value={s} key={i}>
                                    {s}
                                 </MenuItem>
                              ))}
                           </Select>
                        </FormControl>
                     </DataRow>
                     <DataRow>
                        <FormControl size="small" fullWidth>
                           <InputLabel>Tipo de propiedad *</InputLabel>
                           <Select
                              displayEmpty
                              label="Tipo de propiedad *"
                              value={type}
                              onChange={(e) => handleTypeChange(e)}
                           >
                              {Object.values(PropertyType).map((t, i) => (
                                 <MenuItem value={t} key={i}>
                                    {t}
                                 </MenuItem>
                              ))}
                           </Select>
                        </FormControl>
                        <FormControl size="small" fullWidth>
                           <InputLabel>Condominio</InputLabel>
                           <Select
                              displayEmpty
                              label="Condominio"
                              value={condo ? condo : ""}
                              onChange={(e) => {
                                 setCondo(e.target.value)
                                 setZone("")
                              }}
                           >
                              {condos?.map((cond) => (
                                 <MenuItem key={cond.value} value={cond.value}>
                                    {cond.label}
                                 </MenuItem>
                              ))}
                           </Select>
                        </FormControl>
                        <TextField
                           size="small"
                           label="Recámaras *"
                           disabled={isLand || isCommercial}
                           fullWidth
                           value={!isLand && !isCommercial && rooms ? rooms : ""}
                           onChange={(e) => setRooms(+e.target.value)}
                           inputProps={{inputMode: "numeric", pattern: "[0-9]*"}}
                        />
                        <FormControl size="small" fullWidth disabled={isLand}>
                           <InputLabel>Baños *</InputLabel>
                           <Select
                              label="Baños *"
                              value={!isLand && bathrooms ? bathrooms : ""}
                              onChange={(e) => setBathrooms(e.target.value as number)}
                           >
                              {Array.from({length: 49}, (_, i) => 1 + i * 0.5).map((val) => (
                                 <MenuItem key={val} value={val}>
                                    {val}
                                 </MenuItem>
                              ))}
                           </Select>
                        </FormControl>
                        <TextField
                           size="small"
                           label="Cajones de estacionamiento"
                           disabled={isLand}
                           fullWidth
                           value={!isLand && parkingLots ? parkingLots : ""}
                           onChange={(e) => setParkingLots(+e.target.value)}
                           inputProps={{inputMode: "numeric", pattern: "[0-9]*"}}
                        />
                     </DataRow>
                     <DataRow>
                        <TextField
                           size="small"
                           label="Niveles"
                           disabled={isLand}
                           fullWidth
                           value={!isLand && levels ? levels : ""}
                           onChange={(e) => setLevels(+e.target.value)}
                           inputProps={{inputMode: "numeric", pattern: "[0-9]*"}}
                        />
                        <TextField
                           size="small"
                           label="Piso"
                           fullWidth
                           disabled={isLand || isHouse}
                           value={!isLand && !isHouse && floor ? floor : ""}
                           onChange={(e) => setFloor(+e.target.value)}
                           inputProps={{inputMode: "numeric", pattern: "[0-9]*"}}
                        />
                        <TextField
                           size="small"
                           label="Superficie terreno Mts²"
                           fullWidth
                           value={!isApartment && mts ? mts : ""}
                           onChange={(e) => setMts(+e.target.value)}
                           inputProps={{inputMode: "numeric", pattern: "[0-9]*"}}
                           disabled={isApartment}
                        />
                        <TextField
                           size="small"
                           label="Superficie construcción Mts²"
                           disabled={isLand}
                           fullWidth
                           value={!isLand && mtsContruccion ? mtsContruccion : ""}
                           onChange={(e) => setMtsContruccion(+e.target.value)}
                           inputProps={{inputMode: "numeric", pattern: "[0-9]*"}}
                        />
                     </DataRow>
                     <DataRow>
                        <TextField
                           size="small"
                           label="URL CAD"
                           fullWidth
                           value={urlCAD ? urlCAD : ""}
                           onChange={(e) => setUrlCad(e.target.value)}
                        />
                        <TextField
                           size="small"
                           label="Dirección"
                           fullWidth
                           value={address ? address : ""}
                           onChange={(e) => setAddress(e.target.value)}
                        />
                     </DataRow>
                     <DataRow>
                        <TextField
                           size="small"
                           label="Google Maps URL para EMBED"
                           fullWidth
                           value={mapsUrl}
                           onChange={(e) => setMapsUrl(e.target.value)}
                        />
                        <TextField
                           size="small"
                           label="Google Maps URL para Compartir"
                           fullWidth
                           value={mapsShareUrl}
                           onChange={(e) => setMapsShareUrl(e.target.value)}
                        />
                     </DataRow>
                     <AssociatesSelect
                        value={partners}
                        onChange={handleChangePartners}
                        options={partnersFetched}
                        showAllSelected
                     />
                     <h3>Ambientes y otros detalles</h3>
                     <DataRow>
                        {/* seaview */}
                        <FormControl size="small" fullWidth sx={{margin: "0px 2%"}}>
                           <InputLabel id="demo-controlled-open-select-label">
                              Vista al mar *
                           </InputLabel>
                           <Select
                              disabled={!acapulcoSelected}
                              displayEmpty
                              labelId="demo-controlled-open-select-label"
                              id="demo-controlled-open-select"
                              label="Vista al mar *"
                              value={seaview ?? ""}
                              data-testid="seaview-select"
                              onChange={(e) =>
                                 setSeaview(
                                    e.target.value === ""
                                       ? null
                                       : (e.target.value as Exclude<SeaviewOption, null>),
                                 )
                              }
                           >
                              <MenuItem value={"yes"} data-testid="seaview-yes">
                                 Si
                              </MenuItem>
                              <MenuItem value={"no"}>No</MenuItem>
                              <MenuItem value={"partial"}>Vista parcial</MenuItem>
                           </Select>
                        </FormControl>
                        {/* furnished */}
                        <FormControl size="small" fullWidth sx={{margin: "0px 2%"}}>
                           <InputLabel id="demo-controlled-open-select-label">
                              Amueblado *
                           </InputLabel>
                           <Select
                              displayEmpty
                              labelId="demo-controlled-open-select-label"
                              id="demo-controlled-open-select"
                              label="Amueblado *"
                              data-testid="furnished-select"
                              disabled={isLand}
                              value={!isLand && furnished ? furnished : ""}
                              onChange={(e) => {
                                 const value = e.target.value
                                 if (value === "true") {
                                    setFurnished(value)
                                    setFurnishType("Amueblado")
                                 } else {
                                    setFurnished(value)
                                    setFurnishType("Sin muebles")
                                 }
                              }}
                           >
                              <MenuItem value={"true"} data-testid="furnished-true">
                                 Si
                              </MenuItem>
                              <MenuItem value={"false"}>No</MenuItem>
                           </Select>
                        </FormControl>
                        {/* type of furnish */}
                        <FormControl size="small" fullWidth sx={{margin: "0px 2%"}}>
                           <InputLabel id="demo-controlled-open-select-label">
                              Tipo de amueblado
                           </InputLabel>
                           <Select
                              displayEmpty
                              labelId="demo-controlled-open-select-label"
                              id="demo-controlled-open-select"
                              label="Tipo de amueblado"
                              disabled={isLand}
                              value={!isLand && furnishType ? furnishType : ""}
                              onChange={(e) => {
                                 const value = e.target.value
                                 if (value === "Amueblado" || value === "Mobiliario a tratar") {
                                    setFurnished("true")
                                    setFurnishType(value)
                                 } else {
                                    setFurnished("false")
                                    setFurnishType(value)
                                 }
                              }}
                           >
                              {furnishTypes.map((type) => (
                                 <MenuItem value={type} key={type}>
                                    {type}
                                 </MenuItem>
                              ))}
                           </Select>
                        </FormControl>
                     </DataRow>
                     {/* Ambientes */}
                     <FormControl size="small">
                        <InputLabel>Ambientes de la propiedad</InputLabel>
                        <Select
                           multiple
                           disabled={isLand || isCommercial}
                           value={!isLand && !isCommercial && environments ? environments : []}
                           onChange={handleChangeEnvironments}
                           label="Ambientes de la propiedad"
                           renderValue={(selected) => (
                              <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5}}>
                                 {selected.map((value) => (
                                    <Chip
                                       key={value}
                                       label={value}
                                       sx={{bgcolor: "#3f51b5", color: "white"}}
                                    />
                                 ))}
                              </Box>
                           )}
                        >
                           {envs.map((env) => (
                              <MenuItem key={env} value={env}>
                                 {env}
                              </MenuItem>
                           ))}
                        </Select>
                     </FormControl>
                     <FormControl size="small" fullWidth sx={{margin: "0px 2%"}}>
                        <InputLabel id="demo-controlled-open-select-label">
                           Antigüedad de la casa
                        </InputLabel>
                        <Select
                           displayEmpty
                           disabled={!isHouse}
                           labelId="demo-controlled-open-select-label"
                           id="demo-controlled-open-select"
                           label="Antiguedad de la casa"
                           value={isHouse && houseAntiquity ? houseAntiquity : ""}
                           onChange={(e) => setHouseAntiquity(e.target.value)}
                        >
                           <MenuItem value={"En construcción"}>En construcción</MenuItem>
                           <MenuItem value={"A estrenar"}>A estrenar</MenuItem>
                           <MenuItem value={"Hasta 5 años"}>Hasta 5 años</MenuItem>
                           <MenuItem value={"Hasta 10 años"}>Hasta 10 años</MenuItem>
                           <MenuItem value={"Hasta 20 años"}>Hasta 20 años</MenuItem>
                           <MenuItem value={"Hasta 50 años"}>Hasta 50 años</MenuItem>
                        </Select>
                     </FormControl>
                     <DataRow>
                        <FormControl size="small" fullWidth sx={{margin: "0px 2%"}}>
                           <InputLabel id="demo-controlled-open-select-label">
                              Salida a la playa
                           </InputLabel>
                           <Select
                              displayEmpty
                              disabled={!acapulcoSelected || isApartment || isCommercial}
                              labelId="demo-controlled-open-select-label"
                              id="demo-controlled-open-select"
                              label="Salida a la playa"
                              value={
                                 !isApartment && !isCommercial && outingToTheBeach
                                    ? outingToTheBeach
                                    : ""
                              }
                              onChange={(e) => setOutingToTheBeach(e.target.value)}
                           >
                              <MenuItem value={"true"}>Si</MenuItem>
                              <MenuItem value={"false"}>No</MenuItem>
                           </Select>
                        </FormControl>
                     </DataRow>
                     <h3>Información de Venta</h3>
                     <DataRow>
                        <TextField
                           size="small"
                           label="Precio de venta MXN"
                           id="outlined-size-small"
                           fullWidth
                           sx={{margin: "0px 2%"}}
                           value={costoVenta ? costoVenta : ""}
                           onChange={(e) => setCostoVenta(+e.target.value)}
                           inputProps={{inputMode: "numeric", pattern: "[0-9]*"}}
                        />
                        <TextField
                           size="small"
                           label="Precio por metro cuadrado"
                           id="outlined-size-small"
                           fullWidth
                           sx={{margin: "0px 2%"}}
                           value={costoVenta && saleArea ? (costoVenta / saleArea).toFixed(2) : ""}
                           inputProps={{inputMode: "numeric", pattern: "[0-9]*"}}
                           disabled
                        />
                        <TextField
                           size="small"
                           label="Mantenimiento mensual MXN"
                           id="outlined-size-small"
                           fullWidth
                           sx={{margin: "0px 2%"}}
                           value={maintenance ? maintenance : ""}
                           onChange={(e) => setMaintenace(+e.target.value)}
                           inputProps={{inputMode: "numeric", pattern: "[0-9]*"}}
                        />
                     </DataRow>
                     <FormControl size="small">
                        <InputLabel shrink>Comisión de Venta Pactada (%)</InputLabel>
                        <Select
                           displayEmpty
                           label="Comisión de Venta Pactada (%)"
                           value={saleComission ?? ""} // si es null, que muestre la opción vacía
                           onChange={(e) => {
                              const val = e.target.value
                              setSaleComission(val === "" ? null : Number(val))
                           }}
                        >
                           <MenuItem value="">
                              <em>Sin comisión</em>
                           </MenuItem>
                           {Array.from({length: 19}, (_, i) => i * 0.5 + 1).map((i) => (
                              <MenuItem key={i} value={i}>
                                 {i}%
                              </MenuItem>
                           ))}
                        </Select>
                     </FormControl>
                     <h3>Información de Renta</h3>
                     <DataRow>
                        <TextField
                           size="small"
                           label="MXN Renta/noche temporada regular"
                           disabled={isLand || isCommercial}
                           fullWidth
                           value={!isLand && !isCommercial && noche_regular ? noche_regular : ""}
                           onChange={(e) => setNocheRegular(+e.target.value)}
                           inputProps={{inputMode: "numeric", pattern: "[0-9]*"}}
                        />
                        <TextField
                           size="small"
                           label="MXN Renta/noche temporada media"
                           disabled={isLand || isCommercial}
                           fullWidth
                           value={!isLand && !isCommercial && noche_media ? noche_media : ""}
                           onChange={(e) => setNocheMedia(+e.target.value)}
                           inputProps={{inputMode: "numeric", pattern: "[0-9]*"}}
                        />
                        <TextField
                           size="small"
                           label="MXN Renta/noche temporada alta"
                           disabled={isLand || isCommercial}
                           fullWidth
                           value={!isLand && !isCommercial && noche_alta ? noche_alta : ""}
                           onChange={(e) => setNocheAlta(+e.target.value)}
                           inputProps={{inputMode: "numeric", pattern: "[0-9]*"}}
                        />
                     </DataRow>
                     <DataRow>
                        <TextField
                           size="small"
                           label="MXN Renta/mes contrato regular"
                           id="outlined-size-small"
                           fullWidth
                           sx={{margin: "0px 2%"}}
                           value={mensual_regular ? mensual_regular : ""}
                           onChange={(e) => setMensualRegular(+e.target.value)}
                           inputProps={{inputMode: "numeric", pattern: "[0-9]*"}}
                        />
                        <TextField
                           size="small"
                           label="MXN Renta/mes contrato anual"
                           id="outlined-size-small"
                           fullWidth
                           sx={{margin: "0px 2%"}}
                           value={mensual_anual ? mensual_anual : ""}
                           onChange={(e) => setMensualAnual(+e.target.value)}
                           inputProps={{inputMode: "numeric", pattern: "[0-9]*"}}
                        />
                     </DataRow>
                     <DataRow>
                        <TextField
                           size="small"
                           label="Capacidad máxima"
                           fullWidth
                           disabled={isLand || isCommercial}
                           value={!isLand && !isCommercial && capacity ? capacity : ""}
                           onChange={(e) => setCapacity(+e.target.value)}
                           inputProps={{inputMode: "numeric", pattern: "[0-9]*"}}
                        />
                        {/* Pet Friendly */}
                        <FormControl size="small" sx={{margin: "0px 2%", width: "100%"}}>
                           <InputLabel id="demo-controlled-open-select-label">
                              Pet Friendly
                           </InputLabel>
                           <Select
                              displayEmpty
                              labelId="demo-controlled-open-select-label"
                              id="demo-controlled-open-select"
                              disabled={isLand}
                              label="Pet Friendly"
                              value={!isLand && petFriendly ? petFriendly : ""}
                              onChange={(e) => setPetFriendly(e.target.value)}
                           >
                              <MenuItem value={"true"}>Sí</MenuItem>
                              <MenuItem value={"false"}>No</MenuItem>
                           </Select>
                        </FormControl>
                     </DataRow>
                     <h3 className="text-blue">Descripción de la propiedad</h3>
                     {/* description2 */}
                     <TextField
                        size="small"
                        label="Descripción breve (ficha técnica)"
                        id="outlined-size-small"
                        fullWidth
                        multiline
                        sx={{margin: "0px 2%"}}
                        value={desc2 ? desc2 : ""}
                        onChange={(e) => setDesc2(e.target.value)}
                     />
                     {/* description */}
                     <TextField
                        size="small"
                        label="Descripción detallada"
                        id="outlined-size-small"
                        fullWidth
                        multiline
                        sx={{margin: "0px 2%"}}
                        value={desc ? desc : ""}
                        onChange={(e) => setDesc(e.target.value)}
                     />
                     <h3 className="text-blue">Notas de la propiedad</h3>
                     <h3>Notas públicas</h3>
                     {/* public notes */}
                     <TextField
                        size="small"
                        label="Notas públicas"
                        id="outlined-size-small"
                        fullWidth
                        multiline
                        sx={{margin: "0px 2%"}}
                        value={notes ? notes : ""}
                        onChange={(e) => setNotes(e.target.value)}
                     />
                     <h3>Notas privadas</h3>
                     {/* private notes */}
                     <TextField
                        size="small"
                        label="Notas privadas 🔐"
                        id="outlined-size-small"
                        fullWidth
                        multiline
                        sx={{margin: "0px 2%"}}
                        value={privateNotes ? privateNotes : ""}
                        onChange={(e) => setPrivateNotes(e.target.value)}
                     />
                     <h3>Notas al propietario</h3>
                     <TextField
                        size="small"
                        label="Notas al propietario"
                        id="owner-notes"
                        fullWidth
                        multiline
                        sx={{margin: "0px 2%"}}
                        value={ownerNotes ? ownerNotes : ""}
                        onChange={(e) => setOwnerNotes(e.target.value)}
                     />
                     <h3>Imágenes</h3>
                     <div className="w-full my-0 mx-[2%] flex-col space-x-2">
                        <Button
                           className="w-full my-auto mx-[2%] flex space-x-2"
                           variant="outlined"
                           startIcon={<AttachFileIcon />}
                           onClick={() => {
                              const fileInput: any = document.querySelector("#file_input")
                              fileInput?.click()
                           }}
                        >
                           {file ? file.name : "Adjuntar imagen principal"}
                        </Button>
                        <input
                           id="file_input"
                           required
                           data-testid="main-image-input"
                           type="file"
                           style={{display: "none"}}
                           accept=".jpg,.png,.jpeg"
                           onChange={(e) => imageHandler(e)}
                        />
                     </div>
                     {/* image categories */}
                     <div className="w-full my-0 mx-[2%] flex-col space-x-2 ">
                        <div className="w-full my-0 mx-[2%] flex space-x-2">
                           <TextField
                              size="small"
                              label="Categorías de imágenes *"
                              fullWidth
                              value={categoria}
                              onChange={(e) => setCategoria(e.target.value)}
                           />
                           <AddIcon
                              className="text-blue-600 self-center"
                              onClick={handleCategoriasChange}
                              data-testid="add-category-button"
                           />
                        </div>
                        <ReactSortable
                           list={categories}
                           setList={setCategories}
                           className="flex flex-wrap flex-col"
                           delay={isMobile() ? 200 : 0}
                        >
                           {categories.length > 0 &&
                              categories.map((categoria, index) => (
                                 <div
                                    key={categoria.id}
                                    className="flex space-x-4 my-2 border rounded p-5"
                                 >
                                    <p className="w-[100%] overflow-x-scroll overflow-y-hidden">
                                       {categoria.name}
                                    </p>
                                    <EditIcon
                                       className="text-green self-center"
                                       onClick={() => handleImageEdit(index)}
                                       data-testid={`edit-category-${index}`}
                                    />
                                    <DeleteIcon
                                       className="text-red self-center"
                                       onClick={() => {
                                          setConfirmationOpen(true)
                                          setCategoryToDelete(index)
                                       }}
                                    />
                                 </div>
                              ))}
                        </ReactSortable>
                     </div>
                     <ConfirmationDialog
                        open={confirmationOpen}
                        onCancel={() => setConfirmationOpen(false)}
                        onConfirm={() => {
                           setConfirmationOpen(false)
                           handleCategoriaDelete(categoryToDelete)
                        }}
                        message="¿Eliminar categoría de imágenes?"
                     />
                     <h3>Videos</h3>
                     {/* video links */}
                     <div className="w-full my-0 mx-[2%] flex-col space-x-2 ">
                        <div className="w-full my-0 mx-[2%] flex space-x-2">
                           <TextField
                              size="small"
                              label="Links de videos"
                              id="outlined-size-small"
                              fullWidth
                              sx={{margin: "0px 2%"}}
                              value={videoInput}
                              onChange={(e) => setVideoInput(e.target.value)}
                           />
                           <AddIcon className="text-blue-600 self-center" onClick={addVideo} />
                        </div>
                        {videosList.length > 0 &&
                           videosList.map((video, index) => (
                              <div key={video} className="flex space-x-4 my-4 border rounded p-5">
                                 <p className="w-[100%] overflow-x-scroll overflow-y-hidden">
                                    {video}
                                 </p>
                                 <DeleteIcon
                                    className="text-red-600 self-center"
                                    onClick={() => removeVideo(index)}
                                 />
                              </div>
                           ))}
                     </div>
                     {/* add button */}
                     {!loading && (
                        <button
                           className="rounded bg-blue-600 p-2 mb-5 text-white font-bold w-[200px] self-center"
                           onClick={handleSubmit}
                        >
                           Agregar Inmueble
                        </button>
                     )}
                     {loading && (
                        <button
                           className="rounded bg-blue-600 p-2 mb-5 text-white font-bold w-[200px] self-center"
                           disabled
                        >
                           Cargando...
                        </button>
                     )}
                  </>
               ) : (
                  // if showImageForm is true, then this is displayed
                  <UploadImagesForm
                     addImages={addImages}
                     imageName={currImage}
                     images={images[currImage]}
                     closeImageForm={closeImageForm}
                  />
               )}
            </div>
         </>
      </Page>
   )
}

export default AddProperty
